import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatDateTime, healthNameMeta, sortWarnings } from "./statusUtils"

const INITIAL_VISIBLE = 5

function HealthChange({ change }) {
  const from = healthNameMeta(change.from)
  const to = healthNameMeta(change.to)
  return (
    <Row sx={{ alignItems: "center", gap: 0.5 }}>
      <Chip
        size="small"
        label={change.from}
        sx={{ bgcolor: from.bg, color: from.color, fontWeight: 600 }}
      />
      <Typography variant="caption">→</Typography>
      <Chip
        size="small"
        label={change.to}
        sx={{ bgcolor: to.bg, color: to.color, fontWeight: 600 }}
      />
    </Row>
  )
}

HealthChange.propTypes = { change: PropTypes.object.isRequired }

function WarningItem({ warning }) {
  const [open, setOpen] = useState(false)
  const hasDetails = warning.details?.length > 0
  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 96 }}
        >
          {formatDateTime(warning.time)}
        </Typography>
        {warning.change ? <HealthChange change={warning.change} /> : null}
        {warning.severity && warning.severity !== "WARN" ? (
          <Chip size="small" label={warning.severity} color="error" />
        ) : null}
      </Row>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
        {warning.title}
      </Typography>
      {hasDetails ? (
        <>
          <Button
            size="small"
            onClick={() => setOpen((value) => !value)}
            sx={{ px: 0, minWidth: 0, textTransform: "none" }}
          >
            {open ? "Hide details" : `Show details (${warning.details.length})`}
          </Button>
          {open ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {warning.details.map((detail) => (
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  key={detail}
                >
                  {detail}
                </Typography>
              ))}
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  )
}

WarningItem.propTypes = { warning: PropTypes.object.isRequired }

export default function WarningsList({ warnings }) {
  const [showAll, setShowAll] = useState(false)
  const sorted = sortWarnings(warnings)
  const visible = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE)

  return (
    <Column gap={0.5}>
      <Typography variant="overline" color="text.secondary">
        Warnings, last 24 hours
        {sorted.length ? ` (${sorted.length})` : ""}
      </Typography>
      {sorted.length ? (
        <>
          {visible.map((warning) => (
            <WarningItem
              key={`${warning.time}-${warning.title}`}
              warning={warning}
            />
          ))}
          {sorted.length > INITIAL_VISIBLE ? (
            <Button
              size="small"
              onClick={() => setShowAll((value) => !value)}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              {showAll ? "Show fewer" : `Show all ${sorted.length} warnings`}
            </Button>
          ) : null}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          None.
        </Typography>
      )}
    </Column>
  )
}

WarningsList.propTypes = { warnings: PropTypes.array }
